import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';
import GoogleSignIn from '../components/GoogleSignIn';
import { APP_VERSION, BUILD_TIME, GIT_COMMIT } from '../version';

// Build stamp shown in the footer - lets you confirm a deploy landed on
// production without signing in. Regenerated by scripts/generate-version.js.
const buildDate = new Date(BUILD_TIME);
const buildStamp = isNaN(buildDate.getTime())
  ? `v${APP_VERSION}`
  : `v${APP_VERSION} \u00b7 ${buildDate.toLocaleString()} \u00b7 ${GIT_COMMIT}`;

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ssoChecking, setSsoChecking] = useState(true);
  
  // Check if Google Auth is enabled
  const ENABLE_GOOGLE_AUTH = process.env.REACT_APP_ENABLE_GOOGLE_AUTH !== 'false';
  const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

  // Email/password login is disabled in production builds - Google Sign-In only.
  // Set REACT_APP_ENABLE_EMAIL_LOGIN=true to force it on (local development only).
  const ENABLE_EMAIL_LOGIN =
    process.env.REACT_APP_ENABLE_EMAIL_LOGIN === 'true' ||
    (process.env.NODE_ENV !== 'production' &&
      process.env.REACT_APP_ENABLE_EMAIL_LOGIN !== 'false');

  // Check for SSO parameter in URL on mount (Single Sign-On support)
  useEffect(() => {
    let cancelled = false;

    const removeSsoParam = () => {
      const url = new URL(window.location.href);
      if (!url.searchParams.has('sso')) {
        return;
      }
      url.searchParams.delete('sso');
      const newSearch = url.searchParams.toString();
      const newUrl = url.pathname + (newSearch ? `?${newSearch}` : '') + url.hash;
      window.history.replaceState({}, document.title, newUrl);
    };

    const checkSSO = async () => {
      try {
        // Check URL for SSO parameter (from other apps like OpsCostTrack/TWAS)
        const urlParams = new URLSearchParams(window.location.search);
        const ssoToken = urlParams.get('sso');

        if (ssoToken) {
          const success = await authService.loginWithSsoToken(ssoToken);
          removeSsoParam();

          if (success && !cancelled) {
            console.log('✅ SSO session valid - auto-logging in!');
            const user = authService.getUserData();
            const redirectPath = authService.getRedirectPath(user);
            navigate(redirectPath, { replace: true });
            return;
          }

          if (!cancelled) {
            setError('SSO session invalid or expired. Please login again.');
          }
        }

        // Check if user is already logged in (normal session)
        if (authService.isAuthenticated() && !cancelled) {
          const user = authService.getUserData();
          const redirectPath = authService.getRedirectPath(user);
          navigate(redirectPath, { replace: true });
        }
      } catch (error) {
        console.error('SSO check error:', error);
      } finally {
        if (!cancelled) {
          setSsoChecking(false);
        }
      }
    };

    checkSSO();

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!ENABLE_EMAIL_LOGIN) {
      setError('Email login is disabled. Please sign in with Google.');
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const response = await authService.login(email, password);
      
      // Show success message
      console.log('Login successful!');
      
      // Get user data and determine redirect path based on roles
      const user = response.data?.user || authService.getUserData();
      const redirectPath = authService.getRedirectPath(user);
      
      // Redirect based on user role (admin -> /admin/salaries, project_owner -> /dashboard)
      navigate(redirectPath, { replace: true });
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err?.message || 'Login failed. Please check your credentials and try again.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle successful Google login
   */
  const handleGoogleSuccess = () => {
    console.log('Google login successful!');
    const user = authService.getUserData();
    const redirectPath = authService.getRedirectPath(user);
    navigate(redirectPath, { replace: true });
  };

  /**
   * Handle Google login error
   */
  const handleGoogleError = (errorMessage: string) => {
    setError(errorMessage);
  };

  // Show loading state while checking SSO
  if (ssoChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="text-center" role="status" aria-live="polite">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full mb-4 animate-pulse" aria-hidden="true">
            <svg
              className="w-8 h-8 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <p className="text-lg font-semibold text-gray-900">Checking authentication...</p>
          <p className="text-sm text-gray-600 mt-2">Please wait</p>
          <span className="sr-only">Loading authentication status</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <div className="w-full max-w-md">
        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          {/* Logo/Brand */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full mb-4">
              <svg
                className="w-8 h-8 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">
              Project Budgeting System
            </h1>
            <p className="text-gray-600 mt-2">Sign in to continue</p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg" role="alert" aria-live="polite">
              <div className="flex items-start">
                <svg
                  className="w-5 h-5 text-red-600 mt-0.5 mr-2 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                <p className="text-sm text-red-800 font-medium">{error}</p>
              </div>
            </div>
          )}

          {/* Login Form (email/password) - hidden in production */}
          {ENABLE_EMAIL_LOGIN && (
            <>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email Field */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                  placeholder="your.email@example.com"
                  aria-label="Email address"
                  aria-required="true"
                  aria-describedby="email-help"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                <p id="email-help" className="mt-1 text-xs text-gray-500 sr-only">
                  Enter your email address to sign in
                </p>
              </div>

              {/* Password Field */}
              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  placeholder="Enter your password (optional)"
                  aria-label="Password (optional)"
                  aria-describedby="password-help"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                <p id="password-help" className="text-xs text-gray-500 mt-1">
                  Password is optional for email-based authentication
                </p>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading || !email}
                aria-label={loading ? "Signing in, please wait" : "Sign in to your account"}
                aria-busy={loading}
                className="w-full py-3 px-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-lg hover:from-blue-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:from-blue-500 disabled:hover:to-purple-600"
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Signing in...
                  </span>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>
            </>
          )}

          {/* Google Sign-In */}
          {ENABLE_GOOGLE_AUTH && GOOGLE_CLIENT_ID && (
            <>
              {/* Divider - only needed when the email form is shown above */}
              {ENABLE_EMAIL_LOGIN && (
                <div className="relative mt-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-500">Or continue with</span>
                  </div>
                </div>
              )}

              {/* Google Sign-In Button */}
              <div className="mt-6">
                <GoogleSignIn
                  onSuccess={handleGoogleSuccess}
                  onError={handleGoogleError}
                />
              </div>
            </>
          )}

          {/* No sign-in method available (Google not configured) */}
          {!ENABLE_EMAIL_LOGIN && !(ENABLE_GOOGLE_AUTH && GOOGLE_CLIENT_ID) && (
            <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg" role="alert">
              <p className="text-sm text-amber-900">
                Google Sign-In is not configured. Please contact your administrator.
              </p>
            </div>
          )}

          {/* Additional Info */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              🔐 Secure authentication with session management
            </p>
            <p className="text-xs text-gray-500 mt-2">
              ✨ Single Sign-On (SSO) enabled - Login once, access all apps
            </p>
          </div>

        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-sm text-gray-600">
            © 2024 Project Budgeting System
          </p>
          <p
            className="text-xs text-gray-400 mt-1"
            title={`Built ${BUILD_TIME} from commit ${GIT_COMMIT}`}
            data-build-version={APP_VERSION}
            data-build-time={BUILD_TIME}
            data-build-commit={GIT_COMMIT}
          >
            {buildStamp}
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;


